"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Upload, RefreshCw, ImageIcon } from "lucide-react"

const AISkinBeauty = () => {
  const [image, setImage] = useState(null)
  const [enhancedImage, setEnhancedImage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImage(file)
      setEnhancedImage(null)
      setError(null)
    }
  }

  const enhanceImage = async () => {
    if (!image) {
      setError("Please upload an image first.")
      return
    }

    setLoading(true)
    setError(null)

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "AI-Skin-Beauty.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "2a7432d4-6eed-4ae5-84a1-9c2e72486c06")

    const formdata = new FormData()
    formdata.append("image", image)
    formdata.append("retouch_degree", "1.0")
    formdata.append("whitening_degree", "1.0")

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formdata,
      redirect: "follow",
    }

    try {
      const response = await fetch(
        "https://AI-Skin-Beauty.proxy-production.allthingsdev.co/face/editing/face-beauty",
        requestOptions,
      )
      const result = await response.json()

      if (result.error_code === 0 && result.data.image_url) {
        setEnhancedImage(result.data.image_url)
      } else {
        throw new Error(result.error_detail.message || "Failed to enhance image")
      }
    } catch (error) {
      console.error("Error:", error)
      setError("Failed to enhance the image. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <ImageIcon className="inline-block text-pink-600 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">AI Skin Beauty Enhancer</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Enhance your photos with AI-powered skin beautification!
        </p>
      </motion.div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <div className="mb-6">
            <label htmlFor="image-upload" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload an image
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100"
            />
          </div>

          {image && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2 dark:text-white">Original Image</h2>
              <img
                src={URL.createObjectURL(image) || "/placeholder.svg"}
                alt="Original"
                className="max-w-full h-auto rounded-lg shadow-md"
              />
            </div>
          )}

          <button
            onClick={enhanceImage}
            disabled={!image || loading}
            className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 transition-colors duration-300 flex items-center justify-center"
          >
            {loading ? <RefreshCw className="animate-spin mr-2" size={18} /> : <Upload className="mr-2" size={18} />}
            {loading ? "Enhancing..." : "Enhance Image"}
          </button>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 p-4 bg-red-100 text-red-700 rounded-md"
            >
              {error}
            </motion.div>
          )}

          {enhancedImage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <h2 className="text-lg font-semibold mb-2 dark:text-white">Enhanced Image</h2>
              <img
                src={enhancedImage || "/placeholder.svg"}
                alt="Enhanced"
                className="max-w-full h-auto rounded-lg shadow-md"
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AISkinBeauty

